import React, { useEffect, useRef, useState } from "react";

/* =====================================================================
   Event card
   =====================================================================
   One selection per row, beside its own Back and Lay price, under
   labelled columns. Previously both team names were a single sentence on
   the left and six unlabelled boxes sat on the right, so nothing tied a
   name to a number.

   Shared by the home page block and the Cricket / Football / Tennis
   pages, which were four copies of the same markup that had already
   started to drift apart before this.
   ===================================================================== */

// How long a price stays tinted after it moves.
const FLASH_MS = 1600;

const fmtStart = (openDate) => {
  const d = new Date(openDate);
  if (Number.isNaN(d.getTime())) return "";
  return d
    .toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(",", "");
};

const EventCard = ({ item, marketData, onOpen }) => {
  const runners = item?.runners || [];
  const prices = marketData || [];

  // Green and red are status only, so this is the one place they belong on
  // an odds cell: the price just moved. Keyed by selection and side so a
  // re-render for any other reason does not light the whole card up.
  const previous = useRef({});
  const [moved, setMoved] = useState({});

  const priceKeys = runners
    .map((r, i) => {
      const p = prices[i];
      const id = r?.selectionId || i;
      return (
        id +
        ":" +
        (p?.ex?.availableToBack?.[0]?.price || "") +
        ":" +
        (p?.ex?.availableToLay?.[0]?.price || "")
      );
    })
    .join("|");

  useEffect(() => {
    const changed = {};
    runners.forEach((r, i) => {
      const p = prices[i];
      const id = String(r?.selectionId || i);
      [
        ["back", p?.ex?.availableToBack?.[0]?.price],
        ["lay", p?.ex?.availableToLay?.[0]?.price],
      ].forEach(([side, price]) => {
        const key = id + "-" + side;
        const before = previous.current[key];
        if (before !== undefined && price !== undefined && price !== before) {
          changed[key] = price > before ? "up" : "down";
        }
        if (price !== undefined) previous.current[key] = price;
      });
    });

    if (!Object.keys(changed).length) return undefined;
    setMoved((prev) => ({ ...prev, ...changed }));
    const t = setTimeout(() => {
      setMoved((prev) => {
        const next = { ...prev };
        Object.keys(changed).forEach((k) => delete next[k]);
        return next;
      });
    }, FLASH_MS);
    return () => clearTimeout(t);
    // priceKeys collapses every price on the card into one string, so this
    // runs when a number actually changes rather than on every parent
    // render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceKeys]);

  const isLive = new Date() >= new Date(item?.event?.openDate);

  /* A price opens the match page, where the bet slip lives and a guest is
     asked to log in. These were plain spans, so tapping one did nothing
     at all -- the "odds do nothing as a guest" report in #6 was true for
     everyone. */
  const oddClass = (price, key) => {
    if (!price) return "rb-odd rb-odd--empty";
    const dir = moved[key];
    return dir ? "rb-odd rb-odd--" + dir : "rb-odd";
  };

  return (
    <>
      <div className="rb-event__top">
        <span className="rb-event__time">{fmtStart(item?.event?.openDate)}</span>
        {item?.competition?.name && (
          <span className="rb-event__comp">{item.competition.name}</span>
        )}
        <span className={isLive ? "rb-status rb-status--live" : "rb-status rb-status--soon"}>
          {isLive ? "LIVE" : "UPCOMING"}
        </span>
      </div>

      {/* Column labels once per card, rather than the "BM" marker nobody
          outside the building could read. Back and Lay is what these two
          prices actually are. */}
      <div className="rb-event__cols" aria-hidden="true">
        <span />
        <span className="rb-event__cols-inner">
          <span>BACK</span>
          <span>LAY</span>
        </span>
      </div>

      {runners.map((runner, i) => {
        const priced = prices[i];
        const back = priced?.ex?.availableToBack?.[0]?.price;
        const lay = priced?.ex?.availableToLay?.[0]?.price;
        const id = String(runner?.selectionId || i);
        return (
          <div className="rb-event__row" key={runner?.selectionId || i}>
            <span className="rb-event__team">{runner?.runnerName}</span>
            <span className="rb-event__odds">
              <button
                type="button"
                className={oddClass(back, id + "-back")}
                disabled={!back}
                onClick={() => onOpen(item)}
                aria-label={
                  "Back " +
                  (runner?.runnerName || "") +
                  (back ? " at " + back : ", price unavailable")
                }
              >
                {back || "--"}
              </button>
              <button
                type="button"
                className={oddClass(lay, id + "-lay")}
                disabled={!lay}
                onClick={() => onOpen(item)}
                aria-label={
                  "Lay " +
                  (runner?.runnerName || "") +
                  (lay ? " at " + lay : ", price unavailable")
                }
              >
                {lay || "--"}
              </button>
            </span>
          </div>
        );
      })}

      <div className="rb-event__foot">
        {item?.marketName ? (
          <span className="rb-event__tv">{item.marketName}</span>
        ) : (
          <span />
        )}
        <button type="button" className="rb-event__markets" onClick={() => onOpen(item)}>
          All markets &rarr;
        </button>
      </div>
    </>
  );
};

export default EventCard;
